import React, {isValidElement} from "react";

/**
 * Command payload received from the server for notifications (modal, toast, etc.).
 */
export interface NotificationCommand {
    /**
     * The function to execute.
     * Example: `"showModal"` or `"showToast"`.
     */
    func: string;

    /**
     * The type of notification element.
     * Example: `"modal"`, `"toast"`, etc.
     */
    type: string;

    /**
     * Flat array of settings delivered by the API.
     * Example: `["variant","info","class","alarm-modal","id","clock-out"]`
     */
    settings: any[];

    /**
     * Notification content, always provided as an HTML string.
     */
    content: string | React.ReactNode;

    /**
     * Target user ID for which this notification is intended.
     */
    user: number;
}

/**
 * Type guard that checks whether a given object is a valid {@link NotificationCommand}.
 *
 * @param obj - The object to check.
 * @returns `true` if the object matches the {@link NotificationCommand} shape, otherwise `false`.
 */
export function isNotificationCommand(obj: unknown): obj is NotificationCommand {
    return (
        !!obj &&
        typeof obj === "object" &&
        typeof (obj as any).func === "string" &&
        typeof (obj as any).type === "string" &&
        Array.isArray((obj as any).settings) &&
        (typeof (obj as any).content === "string" || isValidElement((obj as any).content)) &&
        typeof (obj as any).user === "number"
    );
}
